import type { DesignDocument } from '@forgd/contract/types'
import { DesignDocument as DesignDocumentSchema } from '@forgd/contract/types'
import { hash } from 'ohash'
import { z } from 'zod'

export const DRAFT_STORAGE_KEY = 'forgd:dt:draft'

export const MODEL_STORAGE_KEY = 'forgd:dt:model'

export const DRAFT_VERSION = 1

export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000

export const MAX_DRAFT_BYTES = 256 * 1024

export const DesignDraftSchema = z.object({
  v: z.literal(DRAFT_VERSION),
  document: DesignDocumentSchema,
  updatedAt: z.string(),
})
export type DesignDraft = z.infer<typeof DesignDraftSchema>

export type DraftRejection = 'empty' | 'malformed' | 'version' | 'expired' | 'too-large'

export type DraftReadResult =
  | { ok: true, draft: DesignDraft }
  | { ok: false, reason: DraftRejection }

export function createDraft(document: DesignDocument, now: number): DesignDraft {
  return {
    v: DRAFT_VERSION,
    document,
    updatedAt: new Date(now).toISOString(),
  }
}

/**
 * Reads a stored draft, rejecting anything we cannot trust.
 *
 * Every rejection is silent by design: a draft is unsaved exploration, so falling back to the
 * seeded model is always a safe outcome - unlike a half-migrated model, which would show numbers
 * nobody chose.
 */
export function parseDraft(raw: string | null | undefined, now: number): DraftReadResult {
  if (!raw) {
    return { ok: false, reason: 'empty' }
  }

  if (raw.length > MAX_DRAFT_BYTES) {
    return { ok: false, reason: 'too-large' }
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  }
  catch {
    return { ok: false, reason: 'malformed' }
  }

  // Check the version before the body: a draft written by an older release is expected to fail
  // the schema, and reporting that as "malformed" would hide a routine upgrade.
  if (!parsed || typeof parsed !== 'object' || (parsed as { v?: unknown }).v !== DRAFT_VERSION) {
    return { ok: false, reason: 'version' }
  }

  const draft = DesignDraftSchema.safeParse(parsed)
  if (!draft.success) {
    return { ok: false, reason: 'malformed' }
  }

  const updatedAt = Date.parse(draft.data.updatedAt)
  if (Number.isNaN(updatedAt) || now - updatedAt > DRAFT_TTL_MS) {
    return { ok: false, reason: 'expired' }
  }

  return { ok: true, draft: draft.data }
}

export type DraftWriteResult =
  | { ok: true, raw: string }
  | { ok: false, reason: 'too-large' }

export function serializeDraft(draft: DesignDraft): DraftWriteResult {
  const raw = JSON.stringify(draft)

  if (raw.length > MAX_DRAFT_BYTES) {
    return { ok: false, reason: 'too-large' }
  }

  return { ok: true, raw }
}

/**
 * Stable key for the dashboard query. The dashboard is a pure function of the model, so two
 * identical models must share a cache entry and any edit must produce a new one.
 */
export function hashModel(model: DesignDocument['model']): string {
  return hash(model)
}
